// "You're leaving early — why?", asked at the moment it matters.
//
// The dialog opens because a clock-out was REFUSED (the punch came back
// `approval_required`), so it has to do two things at once: explain why nothing
// happened when the button was pressed, and turn that into the one action that
// moves things forward. Anything else here would be a second obstacle in front
// of someone who is trying to leave.
//
// The reason is mandatory. An approver reading "wants to leave 2h early" with
// nothing beside it cannot decide, and a request nobody can decide is worse
// than no gate at all.

import { useEffect, useState } from 'react';
import { Clock, Send } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import {
  requestEarlyExit,
  type EarlyExitPrompt,
  type EarlyExitRequestResult,
} from '../../api/attendance';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What the refused clock-out said. */
  prompt: EarlyExitPrompt | null;
  /** Fired on every outcome, including the ones that are not a new request. */
  onResult: (result: EarlyExitRequestResult) => void;
}

const MIN_REASON = 3;

export default function EarlyExitRequestDialog({
  open,
  onOpenChange,
  prompt,
  onResult,
}: Props) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A fresh ask each time: yesterday's typed reason reappearing would be sent
  // by anyone who half-reads the dialog.
  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  const pending = prompt?.pending_request;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await requestEarlyExit(reason.trim());
      onResult(result);
      onOpenChange(false);
    } catch (err: any) {
      setError(err?.message || 'Could not send the request. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-600" />
            Leaving early needs approval
          </DialogTitle>
          <DialogDescription>
            {prompt?.message ||
              'Clocking out before your shift ends has to be approved.'}
          </DialogDescription>
        </DialogHeader>

        {pending ? (
          // Already asked today and still waiting: sending a second identical
          // request would only ring the approver's bell twice.
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <p className="font-medium">
              Your request is already with{' '}
              {pending.approver_name || 'your approver'}.
            </p>
            <p className="mt-1 text-amber-800">
              Reason sent: {pending.reason || '—'}
            </p>
            <p className="mt-1 text-xs text-amber-700">
              Clock Out opens as soon as it is approved.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <label
              htmlFor="early-exit-reason"
              className="text-sm font-medium text-gray-700"
            >
              Why do you need to leave?
            </label>
            <textarea
              id="early-exit-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={1000}
              autoFocus
              placeholder="Dentist appointment at 5, back online tonight"
              className="w-full rounded-md border border-gray-300 p-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500">
              Goes to your approver with the time you asked and how early it is.
            </p>
          </div>
        )}

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            {pending ? 'Close' : 'Cancel'}
          </Button>
          {!pending && (
            <Button
              onClick={submit}
              disabled={busy || reason.trim().length < MIN_REASON}
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              <Send className="mr-1.5 h-3.5 w-3.5" />
              {busy ? 'Sending…' : 'Send for approval'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
